import { ImportResultMessage } from '@/components/container/form/modules/master/csvUpload/csvUploadSchema'

/**
 * RFC 4180 準拠 CSV パーサー
 * ダブルクォート内の改行・カンマ・タブ・エスケープ ("") を正しく処理する。
 * 戻り値: 2次元配列 (各行 = string[])
 */
export const parseCsv = (text: string): string[][] => {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuote = false
  // 区切り文字を自動検出: 最初の非クォート行にタブがあればTSV
  const sep = /^[^"]*\t/.test(text.split(/\r?\n/)[0] || '') ? '\t' : ','

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]

    if (inQuote) {
      if (ch === '"') {
        // ダブルクォートのエスケープ ("") か、フィールド終端か判定
        if (i + 1 < text.length && text[i + 1] === '"') {
          field += '"'
          i++ // エスケープされたクォートをスキップ
        } else {
          inQuote = false
        }
      } else {
        field += ch
      }
    } else {
      if (ch === '"' && field === '') {
        inQuote = true
      } else if (ch === sep) {
        row.push(field.trim())
        field = ''
      } else if (ch === '\r') {
        // \r をスキップ (\r\n 対応)
      } else if (ch === '\n') {
        row.push(field.trim())
        if (row.some((c) => c !== '')) rows.push(row)
        row = []
        field = ''
      } else {
        field += ch
      }
    }
  }
  // 最終行 (末尾改行なし対応)
  if (field !== '' || row.length > 0) {
    row.push(field.trim())
    if (row.some((c) => c !== '')) rows.push(row)
  }
  return rows
}

/**
 * CSV行をレコードオブジェクトに変換する
 * csvHeaders の順序でカラムをマッピング
 */
export const csvToRecords = (rows: string[][], csvHeaders: string[]): Record<string, string>[] => {
  return rows.map((cols) => {
    const record: Record<string, string> = {}
    csvHeaders.forEach((header, i) => {
      if (cols[i] !== undefined && cols[i] !== '') {
        record[header] = cols[i]
      }
    })
    return record
  })
}

/**
 * レコードのバリデーション
 * requiredCsv のカラムが空でないことを確認する
 */
export const validateRecords = (
  rows: string[][],
  csvHeaders: string[],
  requiredCsv: string[]
): {
  valid: string[][]
  errors: ImportResultMessage[]
} => {
  const errors: ImportResultMessage[] = []
  const valid: string[][] = []
  rows.forEach((cols, i) => {
    const rowNum = i + 1
    const rowErrors: string[] = []
    requiredCsv.forEach((col) => {
      const idx = csvHeaders.indexOf(col)
      if (idx === -1 || cols[idx] === '') {
        rowErrors.push(`${col} が空です`)
      }
    })
    if (rowErrors.length > 0) {
      errors.push({ rowNo: rowNum, messages: rowErrors })
    } else {
      valid.push(cols)
    }
  })
  return { valid, errors }
}

export const handleCsvFileImport = (
  file: File,
  onSuccess: (validRows: string[][]) => void,
  onError: (errors: ImportResultMessage[]) => void,
  validateCsv: (text: string) => { valid: string[][]; errors: ImportResultMessage[] }
) => {
  const reader = new FileReader()
  reader.onload = (ev) => {
    const text = ev?.target?.result
    if (text && typeof text === 'string' && text.includes('�')) {
      const reader2 = new FileReader()
      reader2.onload = (ev2) => {
        const result = validateCsv(ev2?.target?.result as string)
        if (result.errors.length > 0) {
          onError(result.errors)
        } else {
          onSuccess(result.valid)
        }
      }
      reader2.readAsText(file, 'Shift_JIS')
    } else {
      const result = validateCsv(text as string)
      if (result.errors.length > 0) {
        onError(result.errors)
      } else {
        onSuccess(result.valid)
      }
    }
  }
  reader.readAsText(file, 'UTF-8')
}
